import {CA_PROVINCE_ABBREVIATIONS, US_STATE_ABBREVIATIONS} from "@/shared/constants";

/**
 * Convert a slug segment to Title Case
 * @example "rocky-point" → "Rocky Point"
 */
function slugToTitleCase(slug: string): string {
    return slug
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
}

/**
 * Parse location slug to human-readable format
 * @param slug - Location slug (e.g., "/usa/New-York/New-York-City")
 * @returns Formatted location string (e.g., "New York City, NY")
 */
export function slugGetLocation(slug?: string): string {
    if (!slug) return "";

    // Strip non-geographic segments (e.g., "vacation-rentals", "markets")
    const IGNORED_SEGMENTS = new Set(["vacation-rentals", "markets"]);

    const rawParts = slug.split("/").filter(Boolean);
    const hasIgnoredSegments = rawParts.some((part) => IGNORED_SEGMENTS.has(part.toLowerCase()));

    // Parse slug: /country/state/city/ or /country/state/
    const parts = rawParts.filter((part) => !IGNORED_SEGMENTS.has(part.toLowerCase()));

    if (parts.length === 0) return "";

    // If URL had ignored segments (markets-style), format remaining parts as Title Case
    if (hasIgnoredSegments) {
        return parts.map((part) => slugToTitleCase(part)).join(", ");
    }

    const index = parts.length === 4 ? 1 : 0;

    const country = parts[index]; // "usa" or "canada"
    const state = parts[index + 1]; // "New-York" or "British-Columbia"
    const city = parts[index + 2]; // "Vancouver" (optional)

    const isStateParts = parts.length === 3 || parts.length === 4;
    // If we have city (3 parts), show: "City, ST"
    if (isStateParts && city) {
        const cityName = city.replace(/-/g, " ");

        // Get state abbreviation
        const stateFormatted = state.replace(/-/g, " ");
        const isUSA = country.toLowerCase() === "usa";

        const stateAbbr = isUSA ? US_STATE_ABBREVIATIONS[state] : CA_PROVINCE_ABBREVIATIONS[state];

        return stateAbbr ? `${cityName}, ${stateAbbr}` : `${cityName}, ${stateFormatted}`;
    }
    // If we have only state (2 parts), show full state name
    else if (parts.length === 2 && state) {
        return state.replace(/-/g, " ");
    }
    // If we have only country
    else if (parts.length === 1) {
        return country.charAt(0).toUpperCase() + country.slice(1);
    }

    return "";
}
